import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { 
  DeviceTokenRequest, 
  DeviceTokenResponse, 
  DeviceTokenListResponse, 
  DeviceTokenCreateResponse,
  DeviceType
} from '../domain/device-token';

@Injectable({
  providedIn: 'root'
})
export class DeviceTokenService {
  private http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  /**
   * Create a new device token registration
   */
  createDeviceToken(deviceTokenRequest: DeviceTokenRequest): Observable<DeviceTokenCreateResponse> {
    return this.http.post<DeviceTokenResponse>(`${this.apiUrl}/device-tokens`, deviceTokenRequest).pipe(
      map(response => ({ success: true, device_token: response })),
      catchError(error => {
        console.error('Error creating device token:', error);
        return throwError(() => ({ 
          success: false, 
          error: error.error?.message || error.message || 'Failed to create device token' 
        }));
      })
    );
  }

  /**
   * Get all device tokens for the current user
   */
  getDeviceTokens(): Observable<DeviceTokenListResponse> {
    return this.http.get<DeviceTokenListResponse>(`${this.apiUrl}/device-tokens`).pipe(
      catchError(error => {
        console.error('Error fetching device tokens:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Invalidate a specific device token
   */
  invalidateDeviceToken(deviceTokenId: string): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/device-tokens/${deviceTokenId}/invalidate`, {}).pipe(
      catchError(error => {
        console.error('Error invalidating device token:', error);
        return throwError(() => error);
      })
    );
  }
  /**
   * Create device token from push subscription data
   */
  createDeviceTokenFromSubscription(
    endpoint: string, 
    p256dhKey: string, 
    authKey: string,
    deviceId?: string
  ): DeviceTokenRequest {
    // Create a device token from the subscription data
    // The backend expects the full subscription data as the device_token
    const subscriptionData = {
      endpoint,
      keys: {
        p256dh: p256dhKey,
        auth: authKey
      }
    };

    return {
      device_token: subscriptionData,
      device_type: 'web',
      device_id: deviceId || this.generateDeviceId(),
      app_version: this.getAppVersion()
    };
  }

  /**
   * Create a device token request for a NATIVE client (Capacitor).
   *
   * Per the frozen `/device-tokens` contract (spec 033,
   * `contracts/device-token-registration.md`), native clients register a bare
   * **FCM registration token string** with `device_type: 'android'` (or `'ios'`
   * when that surface ships) — NOT the Web Push endpoint+keys object the web
   * path sends. The backend discriminates by `device_type` and rejects a shape
   * mismatch with 400.
   *
   * @param fcmToken   The FCM registration token string from
   *                   `PushNotifications.register()`.
   * @param deviceType `'android'` (default) or `'ios'`.
   * @param deviceId   Optional stable per-install id; a persistent one is
   *                   generated when omitted.
   */
  createNativeDeviceToken(
    fcmToken: string,
    deviceType: Extract<DeviceType, 'android' | 'ios'> = 'android',
    deviceId?: string
  ): DeviceTokenRequest {
    return {
      device_token: fcmToken,
      device_type: deviceType,
      device_id: deviceId || this.getNativeDeviceId(),
      app_version: this.getAppVersion()
    };
  }

  /**
   * Stable per-install identifier for the native app.
   *
   * The contract wants a `device_id` that is stable across launches for
   * idempotent upsert and targeted invalidation. Capacitor's push plugin does
   * not expose one and `@capacitor/device` is not installed, so we persist a
   * generated UUID in `localStorage` (available in the native WebView and
   * durable per install). Falls back to a volatile id if storage is
   * unavailable, which still yields a valid (if non-persistent) registration.
   */
  private getNativeDeviceId(): string {
    const STORAGE_KEY = 'rettx.native.device_id';
    try {
      const existing = localStorage.getItem(STORAGE_KEY);
      if (existing) {
        return existing;
      }
      const generated = `native_${this.generateUuid()}`;
      localStorage.setItem(STORAGE_KEY, generated);
      return generated;
    } catch {
      return `native_${this.generateUuid()}`;
    }
  }

  /**
   * RFC4122-ish v4 UUID. Uses `crypto.randomUUID` when available and falls back
   * to a Math.random-based generator for older WebViews.
   */
  private generateUuid(): string {
    const cryptoObj = (globalThis as { crypto?: Crypto }).crypto;
    if (cryptoObj?.randomUUID) {
      return cryptoObj.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Generate a unique device identifier for web clients
   */
  private generateDeviceId(): string {
    // Create a semi-persistent device ID based on browser characteristics
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx!.textBaseline = 'top';
    ctx!.font = '14px Arial';
    ctx!.fillText('Device fingerprint', 2, 2);
    
    const fingerprint = canvas.toDataURL();
    const hash = this.simpleHash(fingerprint + navigator.userAgent + navigator.language);
    
    return `web_${hash}_${Date.now()}`;
  }

  /**
   * Simple hash function for device ID generation
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Get current app version (you might want to get this from package.json or environment)
   */
  private getAppVersion(): string {
    return '1.0.0'; // TODO: Get from package.json or environment
  }
}
